import React, { useState } from 'react';
import '../styles/Login.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import Swal from 'sweetalert2';

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        school_name: '',
        school_type: 'academy',
        phone: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
    const navigate = useNavigate();
    const { fetchSettings } = useSettings();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSignup = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.password_confirmation) {
            setError('Passwords do not match');
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/i;
        if (!passwordRegex.test(formData.password)) {
            setError('Password must be at least 8 chars and contain 1 uppercase, 1 number, and 1 symbol.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/register`, formData);

            Swal.fire({
                icon: 'success',
                title: 'Registration Successful!',
                text: response.data.message || 'Please check your email to verify your account before logging in.',
                confirmButtonColor: '#4361ee'
            }).then(() => {
                navigate('/login');
            });

        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="auth-page">
            {/* Floating Shapes */}
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>

            <div className="auth-card" style={{ maxWidth: '600px' }}>
                <div className="icon-container">
                    <img src="/logo.svg" alt="LMS Logo" className="logo-img" />
                </div>

                <div className="auth-header text-center mb-4">
                    <h3>Create Your Account</h3>
                    <p className="text-muted">Start your journey with LMS</p>
                </div>

                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                <form onSubmit={handleSignup}>
                    <div className="row">
                        <div className="col-md-6 mb-3 text-start">
                            <label className="form-label fw-bold text-muted small">Your Name</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-person"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="name"
                                    placeholder="John Doe"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3 text-start">
                            <label className="form-label fw-bold text-muted small">Email Address</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    placeholder="admin@school.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3 text-start">
                            <label className="form-label fw-bold text-muted small">School/Academy Name</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-building"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="school_name"
                                    placeholder="Excellence Academy"
                                    value={formData.school_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3 text-start">
                            <label className="form-label fw-bold text-muted small">Institution Type</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-mortarboard"></i></span>
                                <select
                                    className="form-select"
                                    name="school_type"
                                    value={formData.school_type}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="academy">🎓 Academy</option>
                                    <option value="school">🏫 School</option>
                                    <option value="institute">🏢 Institute</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 text-start">
                        <label className="form-label fw-bold text-muted small">Phone Number</label>
                        <div className="input-group">
                            <span className="input-group-text"><i className="bi bi-telephone"></i></span>
                            <input
                                type="tel"
                                className="form-control"
                                name="phone"
                                placeholder="+92 300 1234567"
                                value={formData.phone}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3 text-start">
                            <label className="form-label fw-bold text-muted small">Password</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-lock"></i></span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    name="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    minLength="8"
                                />
                                <button
                                    type="button"
                                    className="input-group-text bg-white"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ borderLeft: 'none', cursor: 'pointer' }}
                                >
                                    <i className={`bi ${showPassword ? 'bi-eye-slash text-muted' : 'bi-eye text-primary'}`}></i>
                                </button>
                            </div>
                            <small className="text-muted d-block mt-1 text-nowrap" style={{ fontSize: '0.75rem' }}>
                                Min 8 chars, 1 uppercase, 1 number, & 1 symbol.
                            </small>
                        </div>

                        <div className="col-md-6 mb-4 text-start">
                            <label className="form-label fw-bold text-muted small">Confirm Password</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-lock-fill"></i></span>
                                <input
                                    type={showPasswordConfirmation ? "text" : "password"}
                                    className="form-control"
                                    name="password_confirmation"
                                    placeholder="••••••••"
                                    value={formData.password_confirmation}
                                    onChange={handleChange}
                                    required
                                    minLength="8"
                                />
                                <button
                                    type="button"
                                    className="input-group-text bg-white"
                                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                    style={{ borderLeft: 'none', cursor: 'pointer' }}
                                >
                                    <i className={`bi ${showPasswordConfirmation ? 'bi-eye-slash text-muted' : 'bi-eye text-primary'}`}></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-auth" disabled={loading}>
                        {loading ? (
                            <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Creating Account...</span>
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    <small className="text-muted">Already have an account? <a href="/login" className="text-primary fw-bold hover-underline">Login</a></small>
                </div>
            </div>
        </div>
    );
};

export default Signup;
